import { useEffect, useRef, useState } from "react";
import { Event } from "../dtos/event";
import styles from "./event-display.module.scss";
import baseStyles from "../styles/App.module.scss";
import { DateTime } from "luxon";
import classNames from "classnames";
import Guestlist from "./guestlist/guestlist";
import EventLocation from "../shared/event-location";
import { useLocation } from "react-router-dom";
import { getEvent } from "../api";

function EventDisplay() {
  const location = useLocation(); // Get the current URL location
  const [event, setEvent] = useState(null as Event | null);
  const [loading, setLoading] = useState(true);

  const eventId = location.pathname.split("/")[2];

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const event = await getEvent(eventId);
      console.log(event);
      setEvent(event);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  const handleResponseSubmitted = () => {
    fetchEvent();
  };

  if (loading) return <div>Loading...</div>;
  if (!event) return <div>No event found</div>;
  return (
    <>
      <div className="grid grid-nogutter">
        <div
          className={classNames(
            "col-12 md:col-8 md:col-offset-2 lg:col-6 lg:col-offset-3 xl:col-4 xl:col-offset-4"
          )}
        >
          <EventHeader event={event} />
          <EventDescription description={event.description} />
          <EventLocation location={event.location} />
          <Guestlist
            respondents={event.respondents}
            eventId={eventId}
            onResponseSubmitted={handleResponseSubmitted}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <a href="/">
          <img
            src="/invitify_logo_blue_no_border.png"
            alt="Disco"
            className={styles.logo}
          />
        </a>
      </div>

      <p className={styles.editLink}>
        If you are the host, click <a href={`/edit/${eventId}`}>here</a> to edit.
      </p>
    </>
  );
}

function EventHeader(props: { event: Event }) {
  const formatStartDate = () => {
    if (props.event?.startDate) {
      if (props.event.allday) {
        return DateTime.fromISO(props.event.startDate).toFormat(
          "cccc, LLL dd yyyy"
        );
      } else {
        return DateTime.fromISO(props.event.startDate).toFormat(
          "cccc, LLL dd yyyy, T"
        );
      }
    }
  };

  const formatEndDate = () => {
    if (props.event?.endDate) {
      if (props.event.allday) {
        return DateTime.fromISO(props.event.endDate).toFormat(
          "cccc, LLL dd yyyy"
        );
      } else {
        return DateTime.fromISO(props.event.endDate).toFormat(
          "cccc, LLL dd yyyy, T"
        );
      }
    }
  };

  return (
    <div>
      <div className={styles.imageContainer}>
        <img className={styles.image} src={props.event.imageId} alt="" />
      </div>
      <div
        className={classNames(styles.firstRow, styles.localPadding)}>
        <div className={styles.dateSquareContainer}>
          <DateSquare date={props.event.startDate} />
        </div>
        <div className={styles.textContainer}>
          <h3 className={styles.noMargin}>
            <b>{props.event.displayName}</b>
          </h3>
        </div>
      </div>
      <div className={classNames("grid", "grid-nogutter", styles.localPadding)}>
        <div className={classNames("col-10 center-ver-col", styles.localMarginBottom)}>
          <div>{formatStartDate()}</div>
          <div>{formatEndDate()}</div>
          <a href="#" className={styles.owner}>
            <h4 className={classNames(baseStyles.clickable, styles.noMargin)}>
              @{props.event.hostName}
            </h4>
          </a>
        </div>
      </div>
    </div>
  );
}

function DateSquare(props: { date: string }) {
  const getMonthAbbreviation = () => {
    if (props.date) {
      return DateTime.fromISO(props.date).toFormat("LLL");
    } else {
      return "";
    }
  };

  const getDay = () => {
    if (props.date) {
      return DateTime.fromISO(props.date).toFormat("dd");
    } else {
      return "";
    }
  };

  const getTime = () => {
    if (props.date) {
      return DateTime.fromISO(props.date).toFormat("T");
    } else {
      return "";
    }
  };

  return (
    <div className={styles.maxSize}>
      <div className={styles.square}>
        <div className={styles.circle}>
          <svg viewBox="0 0 55 55">
            <text x="50%" y="36%" textAnchor="middle" className={styles.day}>
              {getDay()}
            </text>
            <text x="50%" y="60%" textAnchor="middle" className={styles.month}>
              {getMonthAbbreviation()}
            </text>
            <line x1="20%" y1="67%" x2="80%" y2="67%" />
            <text x="50%" y="87%" textAnchor="middle" className={styles.time}>
              {getTime()}
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
}

// Function to format description text with markdown-like syntax
const formatDescription = (text: string): JSX.Element[] => {
  if (!text) return [];

  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let key = 0;

  lines.forEach((line, lineIndex) => {
    // Handle horizontal lines (--- or ___ with at least 3 characters)
    if (line.trim().match(/^[-_]{3,}$/)) {
      elements.push(
        <hr key={key++} style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #ccc' }} />
      );
      return;
    }

    // Process each line for bold text and hyperlinks
    const parts: (string | JSX.Element)[] = [];
    let currentText = line;
    let partKey = 0;

    // Process bold text (**text**)
    const boldRegex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(currentText)) !== null) {
      // Add text before the bold
      if (match.index > lastIndex) {
        const beforeText = currentText.slice(lastIndex, match.index);
        parts.push(beforeText);
      }

      // Add bold text
      parts.push(<strong key={`bold-${partKey++}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after the last bold
    if (lastIndex < currentText.length) {
      parts.push(currentText.slice(lastIndex));
    }

    // Process hyperlinks in each part
    const processedParts: (string | JSX.Element)[] = [];

    parts.forEach((part, partIndex) => {
      if (typeof part === 'string') {
        // URL regex for http:// and https://
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        let urlLastIndex = 0;
        let urlMatch;

        while ((urlMatch = urlRegex.exec(part)) !== null) {
          // Add text before the URL
          if (urlMatch.index > urlLastIndex) {
            processedParts.push(part.slice(urlLastIndex, urlMatch.index));
          }

          // Add hyperlink
          processedParts.push(
            <a
              key={`url-${partIndex}-${urlMatch.index}`}
              href={urlMatch[1]}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'blue', textDecoration: 'underline' }}
            >
              {urlMatch[1]}
            </a>
          );
          urlLastIndex = urlMatch.index + urlMatch[0].length;
        }

        // Add remaining text after the last URL
        if (urlLastIndex < part.length) {
          processedParts.push(part.slice(urlLastIndex));
        }
      } else {
        processedParts.push(part);
      }
    });

    // Create the line element
    elements.push(
      <div key={key++} style={{ marginBottom: lineIndex < lines.length - 1 ? '8px' : '0' }}>
        {processedParts}
      </div>
    );
  });

  return elements;
};

function EventDescription(props: { description: string | null | undefined }) {
  const [showFull, setShowFull] = useState(false);
  const [showMoreIcon, setShowMoreIcon] = useState(false);
  const [collapsibleClasses, setCollapsibleClasses] = useState(
    classNames(styles.hideOverflow)
  );
  const minHeight = 300;
  const [collapsibleHeight, setCollapsibleHeight] = useState(`${minHeight}px`);
  const collapsible = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (collapsible && collapsible.current) {
      let maxHeight = collapsible.current.scrollHeight;
      if (showFull) {
        setCollapsibleClasses(classNames(styles.hideOverflow));
        setCollapsibleHeight(`${maxHeight}px`);
      } else {
        setShowMoreIcon(maxHeight > minHeight);
        setCollapsibleClasses(classNames(styles.hideOverflow, styles.gradient));
        setCollapsibleHeight(`${minHeight}px`);
      }
    }
  }, [showFull]);

  const toggleCollapsible = () => {
    setShowFull(!showFull);
  };

  if (props.description) {
    return (
      <div
        className={classNames(styles.localMargin, baseStyles.section)}
      >
        <div
          className={collapsibleClasses}
          style={{ maxHeight: collapsibleHeight }}
          ref={collapsible}
        >
          {formatDescription(props.description)}
        </div>

        {showMoreIcon && (
          <div
            className={classNames(baseStyles.clickable, baseStyles.center)}
            onClick={toggleCollapsible}
          >
            <span
              className={classNames(
                "material-icons-outlined",
                baseStyles.icon36
              )}
            >
              {showFull ? "expand_less" : "expand_more"}
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
}

export default EventDisplay;
